// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { constants } from "@werner/XDL/constants";
import { color, hideElementOnPrint, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import React, { ReactNode, useState } from "react";
import { css } from "styled-components";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    children: ReactNode;
}

interface OwnProps {
    Image: React.FC<{ children?: ReactNode }>;
    Description: React.FC<{ children?: ReactNode }>;
    PartNumbers: React.FC<{ children?: ReactNode }>;
    Price: React.FC<{ children?: ReactNode }>;
    Subtotal: React.FC<{ children?: ReactNode }>;
    Qty: React.FC<{ children?: ReactNode }>;
    Total: React.FC<{ children?: ReactNode }>;
    Availability: React.FC<{ children?: ReactNode }>;
    AvailabilityLink: React.FC<{ children?: ReactNode }>;
    AddToList: React.FC<{ children?: ReactNode }>;
    Remove: React.FC<{ children?: ReactNode }>;
    LineNotesLink: React.FC<{ children?: ReactNode }>;
    LineNotes: React.FC<{ children?: ReactNode }>;
    AddToCartButton: React.FC<{ children?: ReactNode }>;
    Errors: React.FC<{ children?: ReactNode }>;
    PreviouslyPurchasedBadge: React.FC<{ children?: ReactNode }>;
}

export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    partNumbers?: InjectableCss;
    price?: InjectableCss;
    subtotal?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    availability?: InjectableCss;
    availabilityLink?: InjectableCss;
    addToList?: InjectableCss;
    remove?: InjectableCss;
    lineNotesLink?: InjectableCss;
    lineNotes?: InjectableCss;
    addToCartButton?: InjectableCss;
    topSection?: InjectableCss;
    leftSide?: InjectableCss;
    leftSideInfo?: InjectableCss;
    leftSideActions?: InjectableCss;
    rightSide?: InjectableCss;
    notesFieldSection?: InjectableCss;
    addToCartButtonFieldSection?: InjectableCss;
    priceSection?: InjectableCss;
    rightSideActions?: InjectableCss;
    errorsSection?: InjectableCss;
    previouslyPuchasedBadge?: InjectableCss;
}

interface CartLineContainerContextType {
    styles: CartLineContainerStyles;
}

const CartLineContainerContext = React.createContext<CartLineContainerContextType>({
    styles: {},
});

export const useCartLineContainerContext = () => {
    const context = React.useContext(CartLineContainerContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};
const containerClassName = "cartline-container";

const sharedNotesFieldSectionStyles: InjectableCss = {
    css: css`
        display: flex;
        width: 100%;
        margin-top: 8px;
        gap: 22px;
        &:empty {
            display: none;
        }

        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
            &:before {
                display: none;
            }
        }
    `,
};

const sharedAddToCartButtonFieldSectionStyles: InjectableCss = {
    css: css`
        display: flex;
        width: 100%;
        max-width: 740px;
        margin-top: 8px;
        gap: 22px;
        &:empty {
            display: none;
        }

        &:before {
            content: " ";
            width: 100%;
            max-width: 88px;
        }
        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
            &:before {
                display: none;
            }
        }
    `,
};
export const cartlineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 25px 0;
            border-bottom: 1px solid ${color("common.border")};
            &.${containerClassName}-mobile {
                display: none;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                &.${containerClassName}-desktop {
                    display: none;
                }
                &.${containerClassName}-mobile {
                    display: flex;
                    @media print {
                        display: flex;
                        flex-direction: row;
                    }
                }
            }
            @media print {
                display: block;
                break-inside: avoid;
            }
        `,
    },
    image: {
        css: css`
            width: 100px;
            height: 100px;
        `,
    },

    topSection: {
        css: css`
            display: flex;
            gap: 18px;
            width: 100%;
        `,
    },
    leftSide: {
        css: css`
            display: flex;
            width: 100%;
            max-width: 574px;
            gap: 20px;
            margin-top: 8px;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                max-width: 436px;
            }
            @media print {
                gap: 10px;
                max-width: 436px;
            }
        `,
    },
    leftSideInfo: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: calc(100% - 120px);
            & .left-side-details-actions {
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                justify-content: space-between;
                & > div {
                    display: flex;
                    flex-direction: column;
                }
            }
            @media print {
                max-width: calc(100% - 110px);
            }
        `,
    },
    leftSideActions: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            max-width: 245px;
            &:empty {
                display: none;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                max-width: unset;
            }
        `,
    },
    rightSide: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            max-width: calc(100% - 574px);
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-top: 20px;
                max-width: unset;
            }
            @media print {
                margin-top: 0;
                max-width: calc(100% - 436px);
            }
        `,
    },
    notesFieldSection: {
        ...sharedNotesFieldSectionStyles,
    },
    addToCartButtonFieldSection: {
        ...sharedAddToCartButtonFieldSectionStyles,
    },
    priceSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-bottom: 20px;
            }
        `,
    },
    rightSideActions: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 16px;
            &:empty {
                display: none;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-top: 0;
                flex-direction: row;
                justify-content: space-between;
            }
        `,
    },
    availability: {
        css: css`
            display: flex;
            flex: 1;
            align-items: flex-end;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-bottom: 4px;
            }
            ${hideElementOnPrint}
        `,
    },
    lineNotes: {
        css: css`
            width: 100%;
            margin-top: 8px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    remove: {
        css: css`
            display: flex;
            align-items: flex-start;
        `,
    },
    price: {
        css: css`
            flex: 1;
            text-align: center;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
            }
        `,
    },
    subtotal: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
            }
            & > .qty-shipped-ordered {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
        `,
    },
    total: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex: unset;
            }
        `,
    },
    errorsSection: {
        ...mergeWithCss(sharedNotesFieldSectionStyles, {
            css: css`
                margin-top: 16px;
                & > .${containerClassName}-errors {
                    width: 100%;
                }
            `,
        }),
    },
    partNumbers: {
        css: css`
            flex: 1;
        `,
    },
};

const enum NAMES {
    Image = "Image",
    Description = "Description",
    PartNumbers = "PartNumbers",
    Price = "Price",
    Subtotal = "Subtotal",
    Qty = "Qty",
    Total = "Total",
    Availability = "Availability",
    AvailabilityLink = "AvailabilityLink",
    AddToList = "AddToList",
    Remove = "Remove",
    LineNotesLink = "LineNotesLink",
    LineNotes = "LineNotes",
    AddToCartButton = "AddToCartButton",
    Errors = "Errors",
    PreviouslyPurchasedBadge = "PreviouslyPurchasedBadge",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Description),
        getChild(childrenArray, NAMES.PartNumbers),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Subtotal),
        getChild(childrenArray, NAMES.Qty),
        getChild(childrenArray, NAMES.Total),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AvailabilityLink),
        getChild(childrenArray, NAMES.LineNotesLink),
        getChild(childrenArray, NAMES.LineNotes),
        getChild(childrenArray, NAMES.AddToCartButton),
        getChild(childrenArray, NAMES.Remove),
        getChild(childrenArray, NAMES.AddToList),
        getChild(childrenArray, NAMES.Errors),
        getChild(childrenArray, NAMES.PreviouslyPurchasedBadge),
    ];
};

interface RowProps {
    alignItems?: "flex-end" | "center" | "flex-start";
    justifyContent?: "space-between" | "flex-end";
    gap?: number;
}

const CartLineContainer: React.FC<CartLineContainerProps> & OwnProps = ({ children, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));
    const value = React.useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [
        image,
        description,
        partNumbers,
        price,
        subtotal,
        qty,
        total,
        availability,
        availabilityLink,
        lineNotesLink,
        lineNotes,
        addToCartButton,
        remove,
        addToList,
        errors,
        previouslyPurchasedBadge,
    ] = React.useMemo(() => getDestructeredChildren(childrenArray), [childrenArray]);
    return (
        <CartLineContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} className={`${containerClassName}-desktop`}>
                <StyledWrapper {...styles.topSection} className="top-section">
                    <StyledWrapper {...styles.leftSide} className="left-side">
                        {image}
                        <StyledWrapper {...styles.leftSideInfo} className="left-side-info">
                            {description}
                            <StyledWrapper className="left-side-details-actions">
                                <StyledWrapper>
                                    {partNumbers}
                                    {previouslyPurchasedBadge}
                                </StyledWrapper>
                                <StyledWrapper {...styles.leftSideActions}>
                                    {availability}
                                    {availabilityLink}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSide} className="right-side">
                        <StyledWrapper {...styles.priceSection} className="price-section">
                            {qty}
                            {price}
                            {total}
                            {subtotal}
                            {remove}
                        </StyledWrapper>
                        <StyledWrapper {...styles.rightSideActions}>
                            {addToList}
                            {lineNotesLink}
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.addToCartButtonFieldSection}
                            className="addToCartButton-field-section"
                        >
                            {addToCartButton}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.notesFieldSection} className="notes-field-section">
                    {lineNotes}
                </StyledWrapper>
                <StyledWrapper {...styles.errorsSection} className="errors-section">
                    {errors}
                </StyledWrapper>
            </StyledWrapper>
            {/* Mobile */}
            <StyledWrapper {...styles.container} className={`${containerClassName}-mobile`}>
                <StyledWrapper {...styles.leftSide} className="left-side">
                    {image}
                    <StyledWrapper {...styles.leftSideInfo} className="left-side-info">
                        {description}
                        {partNumbers}
                        {previouslyPurchasedBadge}
                        <StyledWrapper {...styles.leftSideActions} className="left-side-actions">
                            {availability}
                            {availabilityLink}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSide} className="right-side">
                    <StyledWrapper {...styles.priceSection} className="price-section">
                        {qty}
                        {price}
                        {subtotal}
                        {total}
                    </StyledWrapper>
                    {lineNotesLink}
                    <StyledWrapper {...styles.notesFieldSection} className="notes-field-section">
                        {lineNotes}
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSideActions} className="right-side-actions">
                        {addToList}
                        {remove}
                    </StyledWrapper>
                    <StyledWrapper {...styles.addToCartButtonFieldSection} className="addToCartButton-field-section">
                        {addToCartButton}
                    </StyledWrapper>
                    <StyledWrapper {...styles.errorsSection} className="errors-section">
                        {errors}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContainerContext.Provider>
    );
};

// Image
CartLineContainer.Image = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.image} className={`${containerClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Image.displayName = NAMES.Image;

// Description
CartLineContainer.Description = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.description} className={`${containerClassName}-description`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Description.displayName = NAMES.Description;
// PartNumbers
CartLineContainer.PartNumbers = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.partNumbers} className={`${containerClassName}-partNumbers`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.PartNumbers.displayName = NAMES.PartNumbers;

// Previously Purchased
CartLineContainer.PreviouslyPurchasedBadge = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.previouslyPuchasedBadge} className={`${containerClassName}-previouslyPurchasedBadge`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.PreviouslyPurchasedBadge.displayName = NAMES.PreviouslyPurchasedBadge;

// Price
CartLineContainer.Price = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.price} className={`${containerClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Price.displayName = NAMES.Price;

// Subtotal
CartLineContainer.Subtotal = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.subtotal} className={`${containerClassName}-subtotal`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Subtotal.displayName = NAMES.Subtotal;

// Qty
CartLineContainer.Qty = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.qty} className={`${containerClassName}-qty`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Qty.displayName = NAMES.Qty;

// Total
CartLineContainer.Total = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.total} className={`${containerClassName}-total`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Total.displayName = NAMES.Total;

// Availability
CartLineContainer.Availability = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.availability} className={`${containerClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Availability.displayName = NAMES.Availability;

// AvailabilityLink
CartLineContainer.AvailabilityLink = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.availabilityLink} className={`${containerClassName}-availabilityLink`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.AvailabilityLink.displayName = NAMES.AvailabilityLink;

// AddToList
CartLineContainer.AddToList = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.addToList} className={`${containerClassName}-addToList`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.AddToList.displayName = NAMES.AddToList;

// Remove
CartLineContainer.Remove = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.remove} className={`${containerClassName}-remove`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Remove.displayName = NAMES.Remove;

// LineNotesLink
CartLineContainer.LineNotesLink = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.lineNotesLink} className={`${containerClassName}-lineNotesLink`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.LineNotesLink.displayName = NAMES.LineNotesLink;

// LineNotes
CartLineContainer.LineNotes = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.lineNotes} className={`${containerClassName}-lineNotes`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.LineNotes.displayName = NAMES.LineNotes;

// AddToCartButton
CartLineContainer.AddToCartButton = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.addToCartButton} className={`${containerClassName}-addToCartButton`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.AddToCartButton.displayName = NAMES.AddToCartButton;

// Errors
CartLineContainer.Errors = ({ children }) => {
    return <StyledWrapper className={`${containerClassName}-errors`}>{children}</StyledWrapper>;
};
CartLineContainer.Errors.displayName = NAMES.Errors;

export default CartLineContainer;
